/**
 * Client-side A/B testing glue.
 *
 * - Stable browser session id in localStorage.
 * - Sticky per-experiment variant assignment (weighted random) in localStorage.
 * - One impression per (session, variant) per session.
 * - Conversion tracking (add_to_cart, begin_checkout, order_completed) writes
 *   one event row per currently-assigned experiment.
 *
 * SSR-safe: every function no-ops when `window` is undefined.
 */
import { supabase } from "@/integrations/supabase/client";

export type AbVariant = {
  id: string;
  key: string;
  label: string;
  blurb: string | null;
  image_url: string | null;
  weight: number;
  is_control: boolean;
};

export type AbExperiment = {
  id: string;
  key: string;
  variants: AbVariant[];
};

const SESSION_KEY = "glg.ab.session_id";
const ASSIGN_KEY = "glg.ab.assignments";
const IMP_PREFIX = "glg.ab.imp:";

function isBrowser() {
  return typeof window !== "undefined";
}

function uuid() {
  if (isBrowser() && "randomUUID" in crypto) return crypto.randomUUID();
  return "ab-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export function getSessionId(): string {
  if (!isBrowser()) return "ssr-session";
  let id = window.localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = uuid();
    try {
      window.localStorage.setItem(SESSION_KEY, id);
    } catch {
      /* quota */
    }
  }
  return id;
}

function loadAssignments(): Record<string, string> {
  if (!isBrowser()) return {};
  try {
    const raw = window.localStorage.getItem(ASSIGN_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function saveAssignments(map: Record<string, string>) {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(ASSIGN_KEY, JSON.stringify(map));
  } catch {
    /* quota */
  }
}

export function getAssignments(): Record<string, string> {
  return loadAssignments();
}

export function resetAssignments() {
  if (!isBrowser()) return;
  window.localStorage.removeItem(ASSIGN_KEY);
}

/**
 * Pick (and persist) the variant for an experiment. Honors `?ab_force=key:variantKey`
 * query strings for QA. Weighted-random across variants.
 */
export function assignVariant(experiment: AbExperiment): AbVariant | null {
  if (!experiment.variants.length) return null;
  const assignments = loadAssignments();
  const existing = assignments[experiment.key];

  // QA override: ?ab_force=home_backyard:v1 (comma-separated for multiple)
  if (isBrowser()) {
    const force = new URLSearchParams(window.location.search).get("ab_force");
    if (force) {
      for (const pair of force.split(",")) {
        const [exp, variantKey] = pair.split(":");
        if (exp === experiment.key) {
          const v = experiment.variants.find((x) => x.key === variantKey);
          if (v) {
            assignments[experiment.key] = v.id;
            saveAssignments(assignments);
            return v;
          }
        }
      }
    }
  }

  if (existing) {
    const v = experiment.variants.find((x) => x.id === existing);
    if (v) return v;
  }

  // Weighted random pick
  const total = experiment.variants.reduce((s, v) => s + Math.max(0, v.weight), 0);
  if (total <= 0) {
    const v = experiment.variants[0];
    assignments[experiment.key] = v.id;
    saveAssignments(assignments);
    return v;
  }
  let r = Math.random() * total;
  for (const v of experiment.variants) {
    r -= Math.max(0, v.weight);
    if (r <= 0) {
      assignments[experiment.key] = v.id;
      saveAssignments(assignments);
      return v;
    }
  }
  const last = experiment.variants[experiment.variants.length - 1];
  assignments[experiment.key] = last.id;
  saveAssignments(assignments);
  return last;
}

async function insertEvent(row: {
  experiment_id: string;
  variant_id: string;
  event_type: "impression" | "add_to_cart" | "begin_checkout" | "order_completed";
  session_id: string;
  user_id?: string | null;
  order_id?: string | null;
  value_cents?: number | null;
}) {
  try {
    await supabase.from("experiment_events").insert(row);
  } catch {
    /* tracking must never throw into the UI */
  }
}

/**
 * Record an impression for a variant, deduped per browser session.
 */
export async function trackImpression(experimentId: string, variantId: string) {
  if (!isBrowser()) return;
  const dedupeKey = IMP_PREFIX + variantId;
  try {
    if (window.sessionStorage.getItem(dedupeKey)) return;
    window.sessionStorage.setItem(dedupeKey, "1");
  } catch {
    /* fall through and still track */
  }
  await insertEvent({
    experiment_id: experimentId,
    variant_id: variantId,
    event_type: "impression",
    session_id: getSessionId(),
  });
}

/**
 * Look up the experiment id for each currently-assigned variant and write a
 * conversion event row per assignment. Caller supplies just the event type
 * and an optional order id / value.
 */
export async function trackAbConversion(
  eventType: "add_to_cart" | "begin_checkout" | "order_completed",
  opts?: { order_id?: string | null; value_cents?: number | null },
) {
  if (!isBrowser()) return;
  const assignments = loadAssignments();
  const variantIds = Object.values(assignments);
  if (!variantIds.length) return;

  try {
    const { data: variants } = await supabase
      .from("experiment_variants")
      .select("id, experiment_id")
      .in("id", variantIds);
    if (!variants?.length) return;
    const session_id = getSessionId();
    const { data: userData } = await supabase.auth.getUser();
    const user_id = userData.user?.id ?? null;
    const rows = variants.map((v) => ({
      experiment_id: v.experiment_id,
      variant_id: v.id,
      event_type: eventType,
      session_id,
      user_id,
      order_id: opts?.order_id ?? null,
      value_cents: opts?.value_cents ?? null,
    }));
    await supabase.from("experiment_events").insert(rows);
  } catch {
    /* swallow */
  }
}
